import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '@/lib/api'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'
const AVATAR_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

const FIELDS = [
  { key: 'company_name',    label: 'Nom légal de la société',    placeholder: 'Velink SAS',              type: 'text' },
  { key: 'company_address', label: 'Adresse du siège social',    placeholder: '12 rue de la Paix, 75001 Paris', type: 'text' },
  { key: 'company_siret',   label: 'Numéro SIRET',               placeholder: '12345678901234',          type: 'text' },
  { key: 'company_email',   label: 'Email de contact',           placeholder: 'contact@velink.fr',       type: 'email' },
  { key: 'commission_rate', label: 'Taux de commission (%)',      placeholder: '10',                      type: 'number' },
  { key: 'cgv_last_update', label: 'Date de mise à jour des CGV', placeholder: '6 juin 2026',            type: 'text' },
]

export default function ParametresAdminPage() {
  const [settings, setSettings] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')

  const avatarRef = useRef<HTMLInputElement>(null)
  const [adminName, setAdminName] = useState('')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarMsg, setAvatarMsg] = useState('')

  useEffect(() => {
    apiFetch<Record<string, string>>('/admin/settings')
      .then((data) => { setSettings(data); setLoading(false) })
      .catch((e: Error) => { setError(e.message); setLoading(false) })

    apiFetch<{ id: string; name: string; avatarUrl: string | null }>('/auth/me')
      .then((data) => {
        setAdminName(data.name)
        if (data.avatarUrl) {
          const [adminId, filename] = data.avatarUrl.split('/')
          fetch(`${API}/api/admin/avatar/${adminId}/${filename}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
          })
            .then((r) => r.ok ? r.blob() : null)
            .then((blob) => blob ? setAvatarPreview(URL.createObjectURL(blob)) : null)
            .catch(() => {})
        }
      })
      .catch(() => {})
  }, [])

  async function uploadAvatar(file: File) {
    if (!AVATAR_MIME.includes(file.type)) { setAvatarMsg('Format non autorisé (JPG, PNG, WebP uniquement)'); return }
    setAvatarUploading(true)
    setAvatarMsg('')
    setAvatarPreview(URL.createObjectURL(file))
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/admin/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
        body: fd,
      })
      if (!res.ok) throw new Error('Erreur lors de l\'upload')
      setAvatarMsg('Avatar mis à jour ✓')
    } catch (e: unknown) {
      setAvatarMsg(e instanceof Error ? e.message : 'Erreur')
      setAvatarPreview(null)
    } finally {
      setAvatarUploading(false)
    }
  }

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true); setMsg(''); setError('')
    const fd = new FormData(e.currentTarget)
    const dto: Record<string, string> = {}
    for (const { key } of FIELDS) {
      dto[key] = (fd.get(key) as string) ?? ''
    }
    dto['cgv_content'] = (fd.get('cgv_content') as string) ?? ''
    try {
      const updated = await apiFetch<Record<string, string>>('/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(dto),
      })
      setSettings(updated)
      setMsg('Paramètres enregistrés.')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-16 glass-card animate-pulse rounded-xl" />)}
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-1">Paramètres de la plateforme</h1>
        <p className="text-text-secondary text-sm">
          Ces informations apparaissent dans les Conditions Générales de Vente et les documents légaux.
        </p>
      </div>

      {/* Avatar admin */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Photo de profil administrateur</h2>
        <div className="flex items-center gap-5">
          <div
            onClick={() => avatarRef.current?.click()}
            className="relative w-20 h-20 rounded-full overflow-hidden cursor-pointer group flex-shrink-0 border border-white/10 hover:border-accent/40 transition-colors"
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-white/[0.06] flex items-center justify-center text-2xl font-bold text-text-muted">
                {adminName?.charAt(0).toUpperCase() ?? 'A'}
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            {avatarUploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
          <input
            ref={avatarRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAvatar(f) }}
          />
          <div>
            <p className="text-text-secondary text-sm">Cliquez sur la photo pour la modifier.</p>
            <p className="text-text-muted text-xs mt-1">JPG, PNG, WebP — max 5 Mo</p>
            {avatarMsg && (
              <p className={`text-xs mt-2 ${avatarMsg.includes('✓') ? 'text-success' : 'text-danger'}`}>{avatarMsg}</p>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="glass-card p-6 space-y-5 mb-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Informations légales</h2>

          {FIELDS.map(({ key, label, placeholder, type }) => (
            <div key={key}>
              <label className="glass-label">{label}</label>
              <input
                name={key}
                type={type}
                defaultValue={settings[key] ?? ''}
                placeholder={placeholder}
                className="glass-input"
                {...(type === 'number' ? { min: 0, max: 100, step: 0.1 } : {})}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : '💾 Enregistrer'}
          </button>
          {msg && <p className="text-success text-sm">{msg}</p>}
        </div>
      </form>

      <div className="glass-card p-6 space-y-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Contenu des CGV</h2>
            <p className="text-text-muted text-xs mt-1">
              Laissez vide pour utiliser les CGV par défaut. Format : <code className="text-accent">## Titre</code> pour les sections, <code className="text-accent">- item</code> pour les listes.
            </p>
          </div>
          <a href="/cgv" target="_blank" className="shrink-0 text-accent text-xs hover:underline">Voir la page →</a>
        </div>
        <textarea
          name="cgv_content"
          defaultValue={settings.cgv_content ?? ''}
          placeholder={"## Article 1 - Objet\n\nLes présentes conditions...\n\n## Article 2 - ...\n\n- Premier point\n- Deuxième point"}
          className="glass-input min-h-[480px] font-mono text-xs leading-relaxed resize-y"
        />
      </div>

      <div className="mt-8 glass-card p-5 rounded-xl">
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Aperçu CGV</p>
        <p className="text-text-secondary text-sm">
          Les valeurs saisies ci-dessus sont affichées dynamiquement sur la page{' '}
          <a href="/cgv" target="_blank" className="text-accent hover:underline">velink.fr/cgv</a>.
        </p>
      </div>
    </div>
  )
}
